import { makeStyles } from 'tss-react/mui';
import { P2GText, ServiceCard, useP2GInView, SvgPrinterNoPrinter, SvgPrinter } from '@parcel2go/core-ui';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { BasketQuote, QuoteExtra } from '~/lib/types/basket-types';
import { Shop } from '~/lib/types/edge-api-types';
import { minorUnitToMajor, toDisplayCurrency } from '~/lib/prices';
import {
  doesQuoteRequirePrinter,
  doesQuoteSupportDdp,
  estimatedDeliveryDateString,
  getRating,
  isQuoteDropOff,
  isSignatureExtrasAvailable,
  isSmsExtrasAvailable,
  maxDimensionsString,
  quoteProtectionOptions,
  classifyDeliveryTag,
  showDimensionsForQuote,
  isDomestic,
  hasVAT,
  isSameDayDelivery
} from '~/lib/quotes';
import { basketAwareProtectionOptions } from '~/lib/basket-aware-protection';
import { Protection } from '~/lib/types/extra-types';
import QuoteCardServiceTypeDropOff from './quote-card-service-type-drop-off';
import QuoteCardServiceTypeCollection from './quote-card-service-type-collection';
import QuoteCardRating from './quote-card-rating';
import QuoteCardServiceLogo from './quote-card-logo';
import SkeletonLoader from '~/components/core/skeleton/skeleton-loader';
import { useSearchParams } from 'react-router-dom';
import { useTaxName, useTenant } from '@parcel2go/npm-p2g-app';
import { sanitiseKey } from '~/lib/utils/translation';
import QuoteCardDrawerContent from './card-drawer-content/quote-card-right-drawer';
import { DrawerNavigationType } from '~/lib/types/enums';
import { PublicSettings } from '~/lib/ui-serverless-settings';
import QuoteCardAdditionalInfo from './card-drawer-content/quote-card-additional-info';

const MemorizedServiceCard = memo(ServiceCard);
const MemorizedQuoteCardRating = memo(QuoteCardRating);
const MemorizedQuoteCardServiceLogo = memo(QuoteCardServiceLogo);
const MemorizedQuoteCardServiceTypeDropOff = memo(QuoteCardServiceTypeDropOff);
const MemorizedQuoteCardServiceTypeCollection = memo(QuoteCardServiceTypeCollection);

const useStyles = makeStyles()((theme) => ({
  printerIcon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(1)
  },
  printerNoPrinterIcon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(1),
    boxSizing: 'inherit'
  },
  printerNotRequiredText: {}
}));

export type QuoteCardProps = {
  quote: BasketQuote;
  onClick: (quote: BasketQuote, extras: string[]) => void;
  shops: Shop[];
  onEnabledGeoLocation?: () => void;
  tab?: string;
  incVat?: boolean;
  index?: number;
  currentSelectedExtras?: QuoteExtra[];
};

const QuoteCard = ({ quote, onClick, shops, tab, incVat, index, currentSelectedExtras }: QuoteCardProps) => {
  const { t } = useTranslation([TranslationNamespaces.QUOTE]);
  const taxName = useTaxName();
  const { classes } = useStyles();
  const [ref, inView] = useP2GInView({ triggerOnce: true, rootMargin: '200px' });
  const [render, setRender] = useState(index === undefined);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedNavigationType, setSelectedNavigationType] = useState<DrawerNavigationType>(
    DrawerNavigationType.Details
  );

  const isDropOff = isQuoteDropOff(quote);

  const [searchParams] = useSearchParams();
  const { settings } = useTenant();
  const isDomesticRoute = isDomestic(searchParams, settings);

  const deliveryTag =
    settings[PublicSettings.QUOTES.ESTIMATEDDELIVERYOPTIONSENABLED] === 'true'
      ? classifyDeliveryTag(quote, isDomesticRoute)
      : undefined;

  const deliveryTagText =
    deliveryTag &&
    t(sanitiseKey(`DELIVERY_TAG_${isDomesticRoute ? 'DOMESTIC' : 'INTERNATIONAL'}_${deliveryTag}`), {
      defaultValue: deliveryTag
    });

  const onQuoteClick = useCallback(
    (includeExtras: boolean) => {
      const protectionOptions = currentSelectedExtras
        ? basketAwareProtectionOptions(quote, currentSelectedExtras)
        : quoteProtectionOptions(quote);

      const selectedProtectionKey =
        currentSelectedExtras && 'selectedKey' in protectionOptions ? protectionOptions.selectedKey : Protection.BASE;

      onClick(
        quote,
        quote.availableExtras.filter((x) => includeExtras && x.key === selectedProtectionKey).map((x) => x.key)
      );
    },
    [onClick, quote, currentSelectedExtras]
  );

  const protection = useMemo(() => {
    return currentSelectedExtras
      ? basketAwareProtectionOptions(quote, currentSelectedExtras)
      : quoteProtectionOptions(quote);
  }, [quote, currentSelectedExtras]);

  const primaryTag = useMemo(() => {
    if (tab) {
      return {
        key: tab,
        type: 'tag-default',
        text: <P2GText component="span">{tab}</P2GText>
      };
    }
    if (isSameDayDelivery(quote)) {
      return {
        key: 'samedaydelivery',
        type: 'tag-default',
        text: <P2GText component="span">{t('TAB_SAME_DAY_DELIVERY', { defaultValue: 'Same Day Delivery' })}</P2GText>
      };
    }

    if (quote.metadata) {
      const tags = Object.keys(quote.metadata || {}).filter((x) => x.startsWith('Tab:'));
      if (tags.length > 0) {
        const t = tags[0].split(':');
        return {
          key: tags[0],
          type: `tag-${t[1].toLocaleLowerCase()}` || 'tag-default',
          text: <P2GText component="span">{quote.metadata[tags[0]]}</P2GText>
        };
      }
    }
  }, [quote, tab, t]);

  const cardTags = useMemo(() => {
    const tags = [];
    if (isSignatureExtrasAvailable(quote)) {
      tags.push(t('TAB_SIGNATURE', { defaultValue: 'Signature Optional' }));
    }
    if (isSmsExtrasAvailable(quote)) {
      tags.push(t('TAB_SMS', { defaultValue: 'SMS Optional' }));
    }
    if (doesQuoteRequirePrinter(quote)) {
      tags.push(t('TAB_PRINTER_REQUIRED', { defaultValue: 'Printer Needed' }));
    }
    if (!doesQuoteRequirePrinter(quote)) {
      tags.push(t('TAB_PRINTER_NOT_REQUIRED', { defaultValue: 'Printer Not Needed' }));
    }
    if (doesQuoteSupportDdp(quote)) {
      tags.push(t('TAB_DDP', { defaultValue: 'Pre-Pay Delivery Duty' }));
    }
    return tags;
  }, [quote, t]);

  const secondaryTags = useMemo(() => {
    const tags = [];
    if (isSignatureExtrasAvailable(quote)) {
      tags.push({
        key: 'signature',
        text: <P2GText component="span">{t('TAB_SIGNATURE', { defaultValue: 'Signature Optional' })}</P2GText>,
        hoverText: (
          <P2GText component="span">
            {t('TAB_SIGNATURE_TOOL', {
              defaultValue: 'Include proof of delivery by adding a signature to your delivery.'
            })}
          </P2GText>
        ),
        backgroundColour: 'white'
      });
    }
    if (isSmsExtrasAvailable(quote)) {
      tags.push({
        key: 'sms',
        text: <P2GText component="span">{t('TAB_SMS', { defaultValue: 'SMS Optional' })}</P2GText>,
        hoverText: (
          <P2GText component="span">
            {t('TAB_SMS_TOOL', {
              defaultValue: 'Enter Your mobile number to recieve tracking updates on your parcel.'
            })}
          </P2GText>
        ),
        backgroundColour: 'white'
      });
    }
    if (doesQuoteRequirePrinter(quote)) {
      tags.push({
        key: 'printerRequired',
        text: (
          <P2GText component="span">
            <SvgPrinter className={classes.printerIcon} width={18} height={16} fill="none" />
            {t('TAB_PRINTER_REQUIRED', { defaultValue: 'Printer Needed' })}
          </P2GText>
        ),
        hoverText: (
          <P2GText component="span">
            {t('TAB_PRINTER_REQUIRED_TOOL', {
              defaultValue: `With this service you will need to print your label at home.`
            })}
          </P2GText>
        ),
        backgroundColour: 'white'
      });
    }
    if (!doesQuoteRequirePrinter(quote)) {
      tags.push({
        key: 'printerNotRequired',
        text: (
          <P2GText component="span" className={classes.printerNotRequiredText}>
            <SvgPrinterNoPrinter className={classes.printerNoPrinterIcon} width={18} height={16} fill="none" />
            {t('TAB_PRINTER_NOT_REQUIRED', { defaultValue: 'Printer Not Needed' })}
          </P2GText>
        ),
        hoverText: (
          <P2GText component="span">
            {t('TAB_PRINTER_NOT_REQUIRED_TOOL', {
              defaultValue: `With this service you will not need to print a label.`
            })}
          </P2GText>
        ),
        backgroundColour: 'white'
      });
    }
    if (doesQuoteSupportDdp(quote)) {
      tags.push({
        key: 'ddp',
        text: <P2GText component="span">{t('TAB_DDP', { defaultValue: 'Pre-Pay Delivery Duty' })}</P2GText>,
        hoverText: (
          <P2GText component="span">
            {t('TAB_DDP_TOOL', {
              defaultValue: 'Have the option to pay the customs duty for your shipment upfront.'
            })}
          </P2GText>
        ),
        backgroundColour: 'white'
      });
    }
    return tags;
  }, [quote, t, classes]);

  const variant = useMemo(() => {
    if (settings[PublicSettings.QUOTES.MINIMALUIENABLED] === 'true') {
      return 'standard';
    }
    return 'fullInfo';
  }, [settings]);

  const rating = useMemo(() => {
    if (settings[PublicSettings.QUOTES.RATINGENABLED] !== 'true') {
      return undefined;
    }

    const quoteRating = getRating(quote);
    return {
      rating: quoteRating.rating,
      hoverText: <MemorizedQuoteCardRating {...quoteRating} />
    };
  }, [quote, settings]);

  useEffect(() => {
    if (inView) {
      const t = setTimeout(
        () => {
          setRender(true);
        },
        50 * (index || 0)
      );
      return () => {
        clearTimeout(t);
      };
    }
    return () => {};
  }, [inView, index]);

  const handleDropOff = useCallback(() => {
    setDrawerOpen(true);
    setSelectedNavigationType(DrawerNavigationType.DropOff);
  }, []);

  const quoteCta = useMemo(() => {
    if (settings[PublicSettings.QUOTES.CTAPROMOTESDDP] === 'true' && !isDomesticRoute) {
      if (doesQuoteSupportDdp(quote)) {
        return t('BOOK_WITH_DDP', { defaultValue: 'Pre-Pay Tax & Duties' });
      }
      return t('BOOK_NO_DDP', { defaultValue: 'Post-Paid Tax & Duties' });
    }
    if (protection.available) {
      if (quote.service.includedProtection) {
        return t('BOOK_WITH_PROTECTION', {
          defaultValue: 'Book with {{amount}} Protection',
          amount: toDisplayCurrency(quote.currency, quote.service.includedProtection, 0)
        });
      }

      return t('BOOK_NO_PROTECTION', { defaultValue: 'Book without Protection' });
    }

    const includedProtection = Math.max(protection.included, quote.service.includedProtection ?? 0);

    if (includedProtection === 0) {
      return t('BOOK_NO_PROTECTION', { defaultValue: 'Book without Protection' });
    }

    if (protection.full) {
      return t('BOOK_WITH_FULL_PROTECTION', { defaultValue: 'Book with full Protection' });
    }

    return t('BOOK_WITH_PROTECTION', {
      defaultValue: 'Book with {{amount}} Protection',
      amount: toDisplayCurrency(quote.currency, includedProtection, 0)
    });
  }, [quote, protection, t, isDomesticRoute, settings]);

  const extraQuoteCta = useMemo(() => {
    if (!protection.available) {
      return undefined;
    }

    if (protection.full) {
      return t('BOOK_WITH_FULL_PROTECTION', { defaultValue: 'Book with full Protection' });
    }

    const includedProtection = Math.max(protection.included, quote.service.includedProtection || 0);

    return t('BOOK_WITH_PROTECTION', {
      defaultValue: 'Book with {{amount}} Protection',
      amount: toDisplayCurrency(quote.currency, includedProtection, 0)
    });
  }, [protection, quote, t]);

  const showExtraOption = protection.available && !quote.service.includedProtection;

  return (
    <div ref={ref}>
      {!render && <SkeletonLoader count={1} />}
      {render && (
        <MemorizedServiceCard
          key={quote.id}
          incVatOnly={incVat}
          currency={quote.currency.code}
          variant={variant}
          serviceName={quote.service.name}
          serviceLogo={<MemorizedQuoteCardServiceLogo slug={quote.service.slug} />}
          closeLabel={t('CLOSE_DRAW', { defaultValue: 'Close' })}
          estimatedDeliveryDate={estimatedDeliveryDateString(quote) || ''}
          estimatedDeliveryLabel={t('ESTIMATED_DELIVERY', { defaultValue: 'Est Delivery' })}
          estimatedDeliveryBoxText={deliveryTagText}
          fromLabel=""
          incVatLabel={t('INCLUDE_VAT', { defaultValue: 'Inc {{taxName}}', taxName: taxName })}
          quoteCta={quoteCta}
          quoteOnClick={() => onQuoteClick(!protection.available)}
          quotePrice={minorUnitToMajor(quote.currency, quote.price.net)}
          quotePriceWithVAT={hasVAT(quote) ? minorUnitToMajor(quote.currency, quote.price.gross) : undefined}
          quotePriceHref={''}
          extraQuoteCta={extraQuoteCta}
          extraQuotePrice={
            showExtraOption ? minorUnitToMajor(quote.currency, protection.net + quote.price.net) : undefined
          }
          extraQuotePriceWithVAT={
            showExtraOption && hasVAT(quote)
              ? minorUnitToMajor(quote.currency, protection.gross + quote.price.gross)
              : undefined
          }
          extraQuotePriceHref={''}
          extraQuoteOnClick={() => onQuoteClick(true)}
          rating={rating}
          primaryTag={primaryTag || undefined}
          secondaryTags={secondaryTags}
          maxDimensions={showDimensionsForQuote(quote) ? maxDimensionsString(quote) : ''}
          drawerLinkText={t('MORE_DETAILS', { defaultValue: 'More Details' })}
          serviceTypeInfo={
            isDropOff ? (
              <MemorizedQuoteCardServiceTypeDropOff shops={shops} onDropoff={handleDropOff} quote={quote} />
            ) : (
              <MemorizedQuoteCardServiceTypeCollection dates={quote.dates} />
            )
          }
          drawerContent={
            <QuoteCardDrawerContent
              quote={quote}
              onClose={() => setDrawerOpen(false)}
              onSelectNavigationType={setSelectedNavigationType}
              selectedNavigationType={selectedNavigationType}
            />
          }
          drawerProps={{ position: 'right', useModalOnMobile: true }}
          isDrawerOpen={drawerOpen}
          onDrawerToggle={setDrawerOpen}
          tags={variant === 'standard' ? cardTags : undefined}
          additionalInfo={
            variant === 'fullInfo' ? undefined : (
              <QuoteCardAdditionalInfo
                quote={quote}
                selectedNavigationType={selectedNavigationType}
                onSelectNavigationType={setSelectedNavigationType}
              />
            )
          }
        />
      )}
    </div>
  );
};

export default QuoteCard;
